import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Activity, CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  // verifying | success | error | invalid
  const [status, setStatus] = useState(token ? "verifying" : "invalid");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    api
      .post("/api/auth/verify-email", { token })
      .then(({ data }) => {
        setStatus("success");
        setMessage(data.message || "Your email address has been verified.");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          formatApiErrorDetail(err?.response?.data?.detail) ||
            "This verification link is invalid or has expired."
        );
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7F9] px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#0047AB] flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900">CertIV Sims</span>
        </div>

        <Card className="border-slate-200 p-7 text-center" data-testid="verify-email-card">
          {status === "verifying" && (
            <div data-testid="verify-email-loading">
              <Loader2 className="w-7 h-7 text-[#0047AB] animate-spin mx-auto" />
              <h1 className="text-xl font-bold text-slate-900 mt-4">Verifying your email…</h1>
              <p className="text-sm text-slate-600 mt-2">Just a moment while we confirm your address.</p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="verify-email-success">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Email verified</h1>
              <p className="text-sm text-slate-600 mt-2">{message}</p>
              <Button
                onClick={() => navigate("/dashboard")}
                data-testid="verify-goto-dashboard"
                className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white"
              >
                Go to my dashboard
              </Button>
            </div>
          )}

          {(status === "error" || status === "invalid") && (
            <div data-testid="verify-email-error">
              <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-6 h-6 text-rose-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Verification failed</h1>
              <p className="text-sm text-slate-600 mt-2">
                {status === "invalid" ? "This verification link is missing its token." : message}
              </p>
              <p className="text-xs text-slate-500 mt-3">
                You can request a fresh verification email from your dashboard under Account settings.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                data-testid="verify-goto-dashboard"
                className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white"
              >
                Go to my dashboard
              </Button>
            </div>
          )}

          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0047AB] mt-5"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </Card>
      </div>
    </div>
  );
}
